import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { RECORDABLE_ABSENCE_REASON_OPTIONS } from '../constants/absenceReasons';
import { dashboardAPI } from '../services/api';
import { MissingAgentRow } from '../utils/attendanceLabels';

interface RecordMissingAgentAbsenceDialogProps {
  agent: MissingAgentRow;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function defaultAbsenceReason(agent: MissingAgentRow): string {
  if (
    agent.status === 'no_show'
    || agent.status === 'no_show_auto'
    || agent.status === 'severely_late'
  ) {
    return 'NO_CALL_NO_SHOW';
  }
  return RECORDABLE_ABSENCE_REASON_OPTIONS[0]?.code || 'SICK';
}

const RecordMissingAgentAbsenceDialog: React.FC<RecordMissingAgentAbsenceDialogProps> = ({
  agent,
  open,
  onClose,
  onSuccess,
}) => {
  const [absenceReason, setAbsenceReason] = useState(() => defaultAbsenceReason(agent));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAbsenceReason(defaultAbsenceReason(agent));
      setNotes('');
      setError(null);
    }
  }, [open, agent]);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await dashboardAPI.recordMissingAgentAbsence({
        userId: agent.id,
        absenceReason,
        notes: notes.trim() || undefined,
      });
      onSuccess();
      handleClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to record absence';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Record absence</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Absence reason</InputLabel>
          <Select
            value={absenceReason}
            label="Absence reason"
            onChange={(e) => setAbsenceReason(e.target.value)}
          >
            {RECORDABLE_ABSENCE_REASON_OPTIONS.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save absence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordMissingAgentAbsenceDialog;
